import * as enabledApi from '@shared/api/enabledPlayApi';
import {
    BetaAllFeatureFlags,
    BetaLicenseFeatureFlags,
    LicenseEnabledFeatureFlags,
    PersonalLicenseEnabledFeatureFlags,
} from '@shared/constants/FeatureFlagValues';
import { DefaultDisplayLocale, DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { StorageIdUser, StorageIdUserAccess } from '@shared/constants/StorageValues';
import { getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import IUserAccess from '@shared/models/components/IUserAccess';
import { AllLicenseTypes } from '@shared/models/organizations/AllLicenseTypes';
import OrganizationModelLicense, {
    OrganizationModelLicenseKeys,
} from '@shared/models/organizations/OrganizationModelLicenses';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import CapabilityFacetGroupModel from '@shared/models/users/CapabilityFacetGroupModel';
import UpdateEmailPreferencesRequest from '@shared/models/users/UpdateEmailPreferencesRequest';
import UpdateUserCapabilitiesRequest from '@shared/models/users/UpdateUserCapabilitiesRequest';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import UserLicenseAccessModel from '@shared/models/users/UserLicenseAccessModel';
import UserModel from '@shared/models/users/UserModel';
import UserPreferencesModel from '@shared/models/users/UserPreferencesModel';
import UserWithCapabilitiesModel from '@shared/models/users/UserWithCapabilitiesModel';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';

const fallbackVoiceUrl = require('../../public/models/en/model.tar.gz');

// todo : vosk language preferences, defaults, and selection
function useCurrentUserContainer() {
    const { i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingFacets, setIsLoadingFacets] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [currentUser, setCurrentUser, currentUserRef] = useStateRef<UserWithCapabilitiesModel | null>(null);
    const [capabilityFacetGroups, setCapabilityFacetGroups] = useState<CapabilityFacetGroupModel[]>([]);
    const [userLicenseAccess, setUserLicenseAccess, userLicenseAccessRef] = useStateRef<UserLicenseAccessModel | null>(
        null
    );
    const [signalRConnected, setSignalRConnected] = useState<boolean>(true);
    const [userLocalePreferences, setUserLocalePreferences, userLocalePreferencesRef] =
        useStateRef<UpdateUserLocaleRequest>({
            defaultLocale: DefaultDisplayLocale,
            defaultVoiceLocale: DefaultVoiceLocale,
        });
    const [showUserFallbackDialog, setShowUserFallbackDialog] = useState<boolean>(false);
    const [voiceModelUrl, setVoiceModelUrl, voiceModelUrlRef] = useStateRef<string>(fallbackVoiceUrl);
    const [isApprovedDomain, setIsApprovedDomain, isApprovedDomainRef] = useStateRef<boolean>(false);
    const [upgradeBannerMap, setUpgradeBannerMap] = useState(new Map<string, IBannerModel>());
    const upgradeBannersArray = Array.from(upgradeBannerMap.values());
    const [isEmailVerified, setIsEmailVerified, isEmailVerifiedRef] = useStateRef<boolean>(false);

    const initializeFromStorage = async (isMainWindow: boolean = true) => {
        try {
            console.log('INITIALIZING USER FROM STORAGE');
            const currentUser = localStorage.getItem(StorageIdUser);
            if (currentUser) {
                const currentUserModel: UserWithCapabilitiesModel = JSON.parse(currentUser);
                setCurrentUser(currentUserModel);
                await updateUserLocales(
                    {
                        defaultLocale: currentUserModel.defaultLocale,
                        defaultVoiceLocale: currentUserModel.defaultVoiceLocale,
                    },
                    isMainWindow
                );
                const userPermission = currentUserModel.isApprovedDomain ?? false;
                setIsApprovedDomain(userPermission);
                setIsEmailVerified(currentUserModel.isEmailVerified ?? false);
                publish('userUpdated', {
                    user: currentUserModel,
                });
            }
        } catch (e) {
            console.log(e);
        }
    };

    const loadCurrentUser = async () => {
        try {
            setIsLoading(true);

            // verify user permissions
            const userAuthPermissions = await enabledApi.validateUserAuthClientPermission();
            let userPermission = false;
            if (
                userAuthPermissions.resultType !== 'Ok' &&
                userAuthPermissions?.errors != null &&
                userAuthPermissions.errors.length > 0
            ) {
                setErrors([...userAuthPermissions.errors]);
            } else if (userAuthPermissions?.resultType == 'Ok') {
                userPermission = true;
            }

            setIsApprovedDomain(userPermission);

            const result = await enabledApi.getUserWithCapabilitiess();
            if (result?.resultType == 'Ok' && result.data) {
                handleUpdateUser(result.data);
                await updateUserLocales({
                    defaultLocale: result.data.defaultLocale,
                    defaultVoiceLocale: result.data.defaultVoiceLocale,
                });
            } else if (result?.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }

            setIsLoading(false);
            return result;
        } catch (e) {
            setIsLoading(false);
            return new UnexpectedResult<UserWithCapabilitiesModel>();
        }
    };

    const updatePassword = async (oldPassword: string, newPassword: string, confirmNewPassword: string) => {
        try {
            setIsLoading(true);
            const result = await enabledApi.changePassword({
                oldPassword,
                newPassword,
                newPasswordConfirmation: confirmNewPassword,
            });

            setIsLoading(false);
            return result;
        } catch (e) {
            setIsLoading(false);
            return new UnexpectedResult<UserModel>();
        }
    };

    const loadCapabilityFacetGroups = async () => {
        try {
            setIsLoadingFacets(true);
            const result = await enabledApi.getCapabilityFacets();
            if (result?.resultType == 'Ok' && result.data) {
                setCapabilityFacetGroups(result.data);
            }
            setIsLoadingFacets(false);

            return result;
        } catch (e) {
            setIsLoadingFacets(false);
            return new UnexpectedResult<CapabilityFacetGroupModel[]>();
        }
    };

    const updateUserCapabilities = async (request: UpdateUserCapabilitiesRequest) => {
        try {
            setIsLoading(true);

            const result = await enabledApi.updateUserCapabilities(request);
            if (result?.resultType == 'Ok' && result.data) {
                handleUpdateUser(result.data);
            }
            setIsLoading(false);

            return result;
        } catch (e) {
            setIsLoading(false);
            return new UnexpectedResult<UpdateUserCapabilitiesRequest>();
        }
    };
    const updateEmailPreferences = async (request: UpdateEmailPreferencesRequest) => {
        try {
            setIsLoading(true);

            const result = await enabledApi.updateUserEmailPreferences(request);
            if (result?.resultType == 'Ok') {
                const updatedUser = { ...currentUser, ...result.data } as UserWithCapabilitiesModel;
                handleUpdateUser(updatedUser);
            }
            setIsLoading(false);

            return result;
        } catch (e) {
            setIsLoading(false);
            return new UnexpectedResult<UpdateUserCapabilitiesRequest>();
        }
    };

    const updateUserPreferences = async (request: UserPreferencesModel) => {
        try {
            setIsLoading(true);

            const result = await enabledApi.updateUserPreferences(request);
            if (result?.resultType == 'Ok') {
                const updatedUser = { ...currentUser, ...result.data } as UserWithCapabilitiesModel; // spread both so we don't lose capability settings and other data
                handleUpdateUser(updatedUser);
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
            setIsLoading(false);

            return result;
        } catch (error) {
            setErrors([...error]);
            setIsLoading(false);
            return new UnexpectedResult<UserModel>();
        }
    };

    const getUserPreferences = async () => {
        try {
            setIsLoading(true);
            const result = await enabledApi.getUserPreferences();
            if (result?.resultType == 'Ok') {
                setIsLoading(false);
                return result;
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
        } catch (error) {
            setErrors([...error]);
            setIsLoading(false);
            return new UnexpectedResult<UserPreferencesModel>();
        }
    };

    const requestEmailVerificationResend = async (): Promise<IResult<UserModel>> => {
        try {
            const result = await enabledApi.requestEmailVerification();
            if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
            return result;
        } catch (error) {
            setErrors([...error]);
            return new UnexpectedResult<UserModel>();
        }
    };

    const clearCurrentUser = () => {
        setCurrentUser(null);
    };

    const updateUserLocales = async (request: UpdateUserLocaleRequest, isMainWindow: boolean = true) => {
        const localePreferences = getValidatedUserLocales(request);

        changeAppLanguage(localePreferences.defaultLocale);

        const modelUrl = await loadVoiceModelUrl(
            localePreferences.defaultVoiceLocale,
            isMainWindow,
            localePreferences.voiceModelUrl
        );

        const hasChanges =
            localePreferences.defaultLocale !== userLocalePreferences.defaultLocale ||
            localePreferences.defaultVoiceLocale !== userLocalePreferences.defaultVoiceLocale;

        if (hasChanges) {
            setUserLocalePreferences({ ...localePreferences, voiceModelUrl: modelUrl });
            publish('userLocalePreferencesUpdated', { localePreferences });
        }
    };

    // gets client supported locale preferences
    const getValidatedUserLocales = (localePreferences: UpdateUserLocaleRequest): UpdateUserLocaleRequest => {
        const defaultLocale = getSupportedLocale(localePreferences.defaultLocale, 'display') ?? DefaultDisplayLocale;
        const defaultVoiceLocale =
            getSupportedLocale(localePreferences.defaultVoiceLocale, 'voice') ?? DefaultVoiceLocale;

        return { defaultLocale, defaultVoiceLocale };
    };

    const changeAppLanguage = (displayLocale: string) => {
        if (i18n.language !== displayLocale) {
            i18n.changeLanguage(displayLocale, (error) => {
                if (error) {
                    console.error(`Unsupported language ${displayLocale}. Defaulting to ${DefaultDisplayLocale}`);
                } else {
                    localStorage.setItem('displayLocale', displayLocale);
                }
            });
        }
    };

    // update database values
    const saveUserLocales = async (request: UpdateUserLocaleRequest): Promise<IResult<UserModel>> => {
        try {
            setIsLoading(true);
            const result = await enabledApi.updateUserLocales(request);
            if (result?.resultType == 'Ok' && result.data) {
                await updateUserLocales(request);
                const updatedUser = { ...currentUser, ...result.data };
                handleUpdateUser(updatedUser);
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
            setIsLoading(false);

            return result;
        } catch (error) {
            setErrors([...error]);
            setIsLoading(false);
            return new UnexpectedResult<UserModel>();
        }
    };

    const handleUpdateUser = (user: UserWithCapabilitiesModel) => {
        user.isApprovedDomain = isApprovedDomainRef.current;
        setCurrentUser(user);
        localStorage.setItem(StorageIdUser, JSON.stringify(user));
        if (user.isEmailVerified !== isEmailVerifiedRef.current) {
            setIsEmailVerified(user.isEmailVerified);
        }
        publish('userUpdated', { user: user });
    };

    const loadVoiceModelUrl = async (voiceLocale: string, isMainWindow: boolean, requestUrl?: string) => {
        let modelUrl = requestUrl ?? localStorage.getItem('VOICE_MODEL_URL') ?? fallbackVoiceUrl;
        try {
            setIsLoading(true);

            if (isMainWindow) {
                const result = await enabledApi.getVoiceModelReferenceForLocale(voiceLocale, 'tar.gz');
                if (result.resultType === 'Ok' && result.data) {
                    modelUrl = result.data.fileUrl;
                }
            }
        } catch (error) {
            setErrors([`Error loading voice model. ${error}`]);
        } finally {
            updateVoiceModelUrl(modelUrl);
            informUserOfFallbackModel(modelUrl, voiceLocale);
            setIsLoading(false);
        }

        return modelUrl;
    };

    const updateVoiceModelUrl = (modelUrl: string) => {
        if (modelUrl !== voiceModelUrlRef.current) {
            localStorage.setItem('VOICE_MODEL_URL', modelUrl);
            setVoiceModelUrl(modelUrl);
        }
    };

    const informUserOfFallbackModel = (modelUrl: string, voiceLocale: string) => {
        if (voiceLocale !== DefaultVoiceLocale && modelUrl === fallbackVoiceUrl) {
            setShowUserFallbackDialog(true);
        }
    };

    const updateUserLicenseAccess = async (access: UserLicenseAccessModel, updateLocal?: boolean) => {
        setUserLicenseAccess(access);
        const type = getUserLicenseAccess(access);
        if (updateLocal) {
            localStorage.setItem(StorageIdUserAccess, JSON.stringify({ access, type }));
        }
        publish('userLicenseChangeLocal', { access, type });
    };

    const loadUserLicenseAccess = async () => {
        try {
            const result = await enabledApi.getUserLicenseAccess();
            if (result?.resultType == 'Ok' && result.data) {
                updateUserLicenseAccess(result.data, true);
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
                loadUserLicenseAccessFromStorage();
            }
        } catch (error) {
            setErrors([error]);
        }
    };

    const loadUserLicenseAccessFromStorage = () => {
        const userAccess = localStorage.getItem(StorageIdUserAccess);
        if (userAccess) {
            const { access, type } = JSON.parse(userAccess) as IUserAccess;
            updateUserLicenseAccess(access, false);
            return { access, type };
        }
    };

    const isFreeUser = () => {
        return !(
            userLicenseAccessRef.current?.isDeveloperAccess ||
            userLicenseAccessRef.current?.isEmployeeAccess ||
            userLicenseAccessRef.current?.isStudentAccess ||
            userLicenseAccessRef.current?.isPlusAccess
        );
    };

    const isProUser = () => {
        return userLicenseAccessRef.current?.isEmployeeAccess;
    };

    const isEduUser = () => {
        return userLicenseAccessRef.current?.isStudentAccess;
    };

    const isSdkUser = () => {
        return userLicenseAccessRef.current?.isDeveloperAccess;
    };

    const isPlusUser = () => {
        return userLicenseAccessRef.current?.isPlusAccess;
    };

    const getUserLicenseAccess = (access: UserLicenseAccessModel) => {
        if (access.isEmployeeAccess) return 'isEmployeeAccess';
        if (access.isStudentAccess) return 'isStudentAccess';
        if (access.isDeveloperAccess) return 'isDeveloperAccess';
        if (access.isPlusAccess) return 'isPlusAccess';
        return 'personal';
    };

    const userLicenseType: AllLicenseTypes = useMemo(() => {
        if (isProUser()) return 'isEmployeeAccess';
        if (isEduUser()) return 'isStudentAccess';
        if (isSdkUser()) return 'isDeveloperAccess';
        if (isPlusUser()) return 'isPlusAccess';

        return 'personal';
    }, [isApprovedDomain, userLicenseAccess]);

    const getUserFeatureFlags = (): string[] => {
        // todo: enforce type safety
        const userFlags: string[] = [];

        userFlags.push(...PersonalLicenseEnabledFeatureFlags);

        // Collect license enabled feature flags for every license the user has access to
        if (userLicenseAccessRef.current) {
            for (const licenseKey of Object.keys(LicenseEnabledFeatureFlags) as OrganizationModelLicenseKeys[]) {
                if (userLicenseAccessRef.current[licenseKey as keyof OrganizationModelLicense]) {
                    userFlags.push(...LicenseEnabledFeatureFlags[licenseKey]);
                }
            }
        }

        // Add beta feature flags that the user has access to
        if (currentUserRef.current?.userPreferences?.featureFlags) {
            for (const flag of currentUserRef.current.userPreferences.featureFlags) {
                if (BetaAllFeatureFlags.includes(flag)) {
                    userFlags.push(flag);
                }
            }
        }

        return [...new Set(userFlags)]; // return unique flags
    };

    const isUserLicensedForAllFeatureFlags = (flagNames: string[]) => {
        if (!userLicenseAccessRef.current) return false;

        return flagNames.every((flag) => isFeatureFlagEnabled(flag));
    };

    const isFeatureFlagEnabled = (flagName: string): boolean => {
        // all personal flags are enabled
        const freeFlags = PersonalLicenseEnabledFeatureFlags.includes(flagName);
        if (freeFlags) {
            return true;
        }

        // check if user has access to the flag
        if (userLicenseAccessRef.current) {
            for (const licenseKey of Object.keys(LicenseEnabledFeatureFlags) as OrganizationModelLicenseKeys[]) {
                if (
                    userLicenseAccessRef.current[licenseKey as keyof OrganizationModelLicense] &&
                    LicenseEnabledFeatureFlags[licenseKey].includes(flagName)
                ) {
                    return true;
                }
            }
            // use userLicenseAccess for beta flags
        }

        // check if user has beta access
        const betaFlags = BetaAllFeatureFlags.includes(flagName);
        if (!betaFlags) {
            return false;
        }
        return currentUserRef.current?.userPreferences?.featureFlags?.includes(flagName) ?? false;
    };

    const userBetaFeatureFlags = useMemo(() => {
        const result: string[] = [...BetaLicenseFeatureFlags['personal']];
        if (isProUser()) {
            result.push(...BetaLicenseFeatureFlags['isEmployeeAccess']);
        }
        if (isEduUser()) {
            result.push(...BetaLicenseFeatureFlags['isStudentAccess']);
        }
        if (isSdkUser()) {
            result.push(...BetaLicenseFeatureFlags['isDeveloperAccess']);
        }
        if (isPlusUser()) {
            result.push(...BetaLicenseFeatureFlags['isPlusAccess']);
        }
        const uniqueFlags = [...new Set(result)];
        return uniqueFlags;
    }, [userLicenseAccess]);

    const updateUserPermissions = (value: boolean) => {
        setIsApprovedDomain(value);
        const currUser = currentUserRef.current;
        if (currUser) {
            handleUpdateUser(currUser);
        }
    };

    const hasNoOrgMemberships = useMemo(() => {
        return (userLicenseAccessRef.current?.organizationLicenses?.length ?? 0) === 0;
    }, [userLicenseAccess]);

    const isLicenseExpired = useMemo(() => {
        return userLicenseAccessRef.current?.licenseExpirationDate
            ? new Date(userLicenseAccessRef.current.licenseExpirationDate) < new Date()
            : false;
    }, [userLicenseAccess]);

    const addUpgradeBanner = (newBanner: IBannerModel, duration?: number) => {
        setUpgradeBannerMap((prevBannerMap) => {
            const updatedMap = new Map(prevBannerMap);

            // Clear the existing timeout if the banner already exists
            if (updatedMap.has(newBanner.id)) {
                const existingBanner = updatedMap.get(newBanner.id);
                if (existingBanner && existingBanner.timeout) {
                    clearTimeout(existingBanner.timeout);
                }
            }

            let timeout: NodeJS.Timeout | undefined;

            // Set a new timeout to remove the banner if duration is provided
            if (duration !== undefined) {
                timeout = setTimeout(() => {
                    setUpgradeBannerMap((currentMap) => {
                        const newMap = new Map(currentMap);
                        newMap.delete(newBanner.id);
                        return newMap;
                    });
                }, duration);
            }

            // Add the banner with the timeout if it exists
            updatedMap.set(newBanner.id, { ...newBanner, timeout });

            return updatedMap;
        });
    };

    const removeUpgradeBanner = (id: string) => {
        setUpgradeBannerMap((prevBannerMap) => {
            const updatedMap = new Map(prevBannerMap);
            const banner = updatedMap.get(id);
            if (banner && banner.timeout) {
                clearTimeout(banner.timeout);
            }
            updatedMap.delete(id);

            return updatedMap;
        });
    };

    useEffect(() => {
        const featureFlags = getUserFeatureFlags();
        publish('userFeatureFlagsUpdated', { featureFlags });
    }, [currentUser, userLicenseAccess]);

    useEffect(() => {
        const unsubsribeUpdateUserLicenseAccess = subscribe('updateUserLicenseAccess', async () => {
            await loadUserLicenseAccess();
        });

        return () => {
            unsubsribeUpdateUserLicenseAccess();
        };
    });

    return {
        currentUser,
        currentUserRef,
        clearCurrentUser,
        capabilityFacetGroups,
        loadCurrentUser,
        initializeFromStorage,
        updatePassword,
        updateUserCapabilities,
        loadCapabilityFacetGroups,
        getUserPreferences,
        updateEmailPreferences,
        updateUserPreferences,
        addUpgradeBanner,
        removeUpgradeBanner,
        upgradeBannersArray,
        requestEmailVerificationResend,
        userLicenseAccess,
        userLicenseAccessRef,
        updateUserLicenseAccess,
        loadUserLicenseAccess,
        loadUserLicenseAccessFromStorage,
        isLoadingFacets,
        isLoading,
        errors,
        userLocalePreferences,
        userLocalePreferencesRef,
        updateUserLocales,
        saveUserLocales,
        handleUpdateUser,
        voiceModelUrl,
        voiceModelUrlRef,
        showUserFallbackDialog,
        setShowUserFallbackDialog,
        isEmailVerified,
        isEmailVerifiedRef,
        isUserLicensedForAllFeatureFlags,
        isFeatureFlagEnabled,
        isFreeUser,
        isProUser,
        isEduUser,
        isSdkUser,
        userLicenseType,
        isApprovedDomain,
        isApprovedDomainRef,
        updateUserPermissions,
        hasNoOrgMemberships,
        isLicenseExpired,
        userBetaFeatureFlags,
        signalRConnected,
        setSignalRConnected,
    };
}

const CurrentUserContainer = createContainer(useCurrentUserContainer);
export default CurrentUserContainer;
